import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { initCatEyeTracker, cleanupCatEyeTracker } from '../utils/catRenderer';

const Home = () => {
  const location = useLocation();
  const [isPreview, setIsPreview] = useState(false);

  useEffect(() => {
    // Check if this is a preview mode
    const params = new URLSearchParams(location.search);
    const previewTheme = params.get('preview');
    const hideNav = params.get('hideNav');
    
    if (previewTheme) {
      setIsPreview(true);
      
      // Dynamically load theme CSS if needed
      const loadThemeCSS = async () => {
        try {
          // Reset theme first
          document.documentElement.removeAttribute('data-theme');
          
          // Load the appropriate CSS file (only cat themes are supported)
          if (previewTheme === 'cat-premium') {
            await import('../styles/cat-premium.css');
          } else if (previewTheme === 'cat-premium-dark') {
            await import('../styles/cat-premium-dark.css');
          }
          
          // Give the CSS a moment to be parsed and applied
          await new Promise(resolve => setTimeout(resolve, 200));
          
          // Apply the preview theme after CSS is loaded
          document.documentElement.setAttribute('data-theme', previewTheme);
          
          // If it's a Halloween theme, ensure proper body classes
          if (previewTheme === 'dark') {
            document.body.classList.add('dark-mode');
            document.body.classList.remove('light-mode');
          } else if (previewTheme === 'cat-premium') {
            // Cat theme uses light mode styling
            document.body.classList.remove('dark-mode');
            document.body.classList.add('light-mode');
            // Initialize cat eye tracker
            setTimeout(() => {
              initCatEyeTracker();
            }, 500);
          } else if (previewTheme === 'cat-premium-dark') {
            // Cat dark theme uses dark mode styling
            document.body.classList.add('dark-mode');
            document.body.classList.remove('light-mode');
            // Initialize cat eye tracker
            setTimeout(() => {
              initCatEyeTracker();
            }, 500);
          } else {
            document.body.classList.remove('dark-mode');
            document.body.classList.add('light-mode');
          }
        } catch (error) {
          console.error('Error loading theme CSS:', error);
          // Still apply the theme attribute even if CSS fails to load
          document.documentElement.setAttribute('data-theme', previewTheme);
        }
      };
      
      loadThemeCSS();
      
      // Hide navbar if requested
      if (hideNav === 'true') {
        // Use MutationObserver to ensure navbar gets hidden even if it loads after this script
        const hideNavbar = () => {
          const navbar = document.querySelector('.navbar');
          if (navbar) {
            navbar.style.display = 'none';
          }
        };
        
        hideNavbar();
        
        // Also observe for navbar being added to DOM
        const observer = new MutationObserver(() => {
          hideNavbar();
        });
        
        observer.observe(document.body, { childList: true, subtree: true });
        
        // Cleanup observer after a short time
        setTimeout(() => {
          observer.disconnect();
        }, 2000);
      }
    }

    // Cleanup function
    return () => {
      const params = new URLSearchParams(location.search);
      const previewTheme = params.get('preview');
      if (previewTheme === 'cat-premium' || previewTheme === 'cat-premium-dark') {
        cleanupCatEyeTracker();
      }
    };
  }, [location]);

  return (
    <div className="container" style={isPreview ? { paddingTop: '20px' } : {}}>
      {/* Hero Section */}
      <div className="card" style={{
        textAlign: 'center',
        padding: '80px 40px',
        marginBottom: '60px',
        background: 'linear-gradient(135deg, var(--card-bg) 0%, var(--card-bg-secondary) 100%)',
        border: '1px solid var(--border-color)',
        animation: 'fadeInUp 1s ease-out'
      }}>
        <div style={{
          fontSize: '64px',
          marginBottom: '24px',
          animation: 'float 3s ease-in-out infinite'
        }}>
          🎮
        </div>
        <h1 style={{
          fontSize: '48px',
          marginBottom: '20px',
          color: 'var(--text-accent)',
          fontWeight: '800',
          textTransform: 'uppercase',
          letterSpacing: '2px',
          background: 'linear-gradient(135deg, var(--btn-primary), var(--btn-secondary))',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          Welcome to GamersUnion
        </h1>
        <p style={{
          fontSize: '20px',
          marginBottom: '40px',
          color: 'var(--text-secondary)',
          lineHeight: '1.6',
          maxWidth: '600px',
          margin: '0 auto 40px'
        }}>
          Discover amazing gamers and indie game developers in one place. Connect, share, and support the gaming community.
        </p>

        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '20px',
          flexWrap: 'wrap'
        }}>
          <Link to="/community">
            <button style={{
              fontSize: '18px',
              padding: '16px 32px',
              background: 'linear-gradient(135deg, var(--btn-primary), var(--btn-primary-hover))',
              color: 'var(--text-light)',
              border: 'none',
              borderRadius: '30px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
              textDecoration: 'none',
              display: 'inline-block'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-3px)';
              e.target.style.boxShadow = '0 10px 30px rgba(102, 126, 234, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
            }}>
              Explore Community
            </button>
          </Link>
          <Link to="/indie-games">
            <button style={{
              fontSize: '18px',
              padding: '16px 32px',
              background: 'linear-gradient(135deg, var(--btn-secondary), var(--btn-secondary-hover))',
              color: 'var(--text-light)',
              border: 'none',
              borderRadius: '30px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 6px 20px rgba(118, 75, 162, 0.4)',
              textDecoration: 'none',
              display: 'inline-block'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-3px)';
              e.target.style.boxShadow = '0 10px 30px rgba(118, 75, 162, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 6px 20px rgba(118, 75, 162, 0.4)';
            }}>
              Browse Indie Games
            </button>
          </Link>
        </div>
      </div>

      {/* Features Section */}
      <div style={{ marginTop: '40px' }}>
        <h2 style={{
          color: 'var(--text-accent)',
          marginBottom: '40px',
          textAlign: 'center',
          fontSize: '32px',
          fontWeight: '700',
          textTransform: 'uppercase',
          letterSpacing: '1px'
        }}>
          Why Choose GamersUnion?
        </h2>
        <div className="grid">
          <div className="card" style={{
            textAlign: 'center',
            padding: '40px 24px',
            animation: 'fadeInUp 0.8s ease-out 0.2s both'
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '20px',
              animation: 'pulse 2s ease-in-out infinite'
            }}>
              🎮
            </div>
            <h3 style={{
              color: 'var(--btn-primary)',
              marginBottom: '16px',
              fontSize: '20px',
              fontWeight: '700'
            }}>
              Community Cards
            </h3>
            <p style={{
              color: 'var(--text-secondary)',
              lineHeight: '1.6',
              fontSize: '15px'
            }}>
              Create your gaming profile with social links, donation options, and more. Connect with fellow gamers and build lasting friendships.
            </p>
          </div>

          <div className="card" style={{
            textAlign: 'center',
            padding: '40px 24px',
            animation: 'fadeInUp 0.8s ease-out 0.4s both'
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '20px',
              animation: 'pulse 2s ease-in-out infinite 0.5s'
            }}>
              🎯
            </div>
            <h3 style={{
              color: 'var(--btn-secondary)',
              marginBottom: '16px',
              fontSize: '20px',
              fontWeight: '700'
            }}>
              Indie Games Showcase
            </h3>
            <p style={{
              color: 'var(--text-secondary)',
              lineHeight: '1.6',
              fontSize: '15px'
            }}>
              Indie developers can showcase their games with descriptions, pricing, and store links. Support independent creators and discover hidden gems.
            </p>
          </div>

          <div className="card" style={{
            textAlign: 'center',
            padding: '40px 24px',
            animation: 'fadeInUp 0.8s ease-out 0.6s both'
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '20px',
              animation: 'pulse 2s ease-in-out infinite 1s'
            }}>
              ✅
            </div>
            <h3 style={{
              color: 'var(--btn-success)',
              marginBottom: '16px',
              fontSize: '20px',
              fontWeight: '700'
            }}>
              Quality Control
            </h3>
            <p style={{
              color: 'var(--text-secondary)',
              lineHeight: '1.6',
              fontSize: '15px'
            }}>
              All submissions are reviewed by admins to ensure quality content. We maintain high standards to provide the best experience for our community.
            </p>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div style={{ 
        marginTop: '80px',
        padding: '60px 40px',
        backgroundColor: 'var(--card-bg)',
        borderRadius: '20px',
        border: '1px solid var(--border-color)',
        animation: 'fadeInUp 1s ease-out'
      }}>
        <h2 style={{
          color: 'var(--text-accent)',
          marginBottom: '40px',
          textAlign: 'center',
          fontSize: '36px',
          fontWeight: '800',
          textTransform: 'uppercase',
          letterSpacing: '2px'
        }}>
          How It Works
        </h2>
        
        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          color: 'var(--text)',
          fontSize: '16px',
          lineHeight: '1.8'
        }}>
          <p style={{ marginBottom: '20px', textAlign: 'center', fontSize: '18px', color: 'var(--text-secondary)' }}>
            Pretty simple. Sign up, discover indie games, join giveaways, earn points for being active. Here's the basics:
          </p>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '30px',
            marginTop: '40px'
          }}>
            <div style={{
              padding: '30px',
              backgroundColor: 'var(--card-bg-secondary)',
              borderRadius: '12px',
              border: '2px solid var(--btn-primary)',
              transition: 'transform 0.3s ease'
            }}>
              <div style={{ fontSize: '40px', marginBottom: '16px' }}>1️⃣</div>
              <h3 style={{ 
                color: 'var(--btn-primary)', 
                fontSize: '22px', 
                fontWeight: '700',
                marginBottom: '12px' 
              }}>
                Make an Account
              </h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                Takes a minute. Pick your type (Gamer, Indie Developer, or Content Creator), choose a username, 
                and you're in. Each type has different features depending on what you're here for.
              </p>
            </div>

            <div style={{
              padding: '30px',
              backgroundColor: 'var(--card-bg-secondary)',
              borderRadius: '12px',
              border: '2px solid var(--btn-secondary)',
              transition: 'transform 0.3s ease'
            }}>
              <div style={{ fontSize: '40px', marginBottom: '16px' }}>2️⃣</div>
              <h3 style={{ 
                color: 'var(--btn-secondary)', 
                fontSize: '22px', 
                fontWeight: '700',
                marginBottom: '12px' 
              }}>
                Start Exploring
              </h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                Check out indie games, enter giveaways, read and write reviews, post in community cards, claim daily 
                rewards, compete on the leaderboard. Everything you do earns points.
              </p>
            </div>

            <div style={{
              padding: '30px',
              backgroundColor: 'var(--card-bg-secondary)',
              borderRadius: '12px',
              border: '2px solid var(--btn-success)',
              transition: 'transform 0.3s ease'
            }}>
              <div style={{ fontSize: '40px', marginBottom: '16px' }}>3️⃣</div>
              <h3 style={{ 
                color: 'var(--btn-success)', 
                fontSize: '22px', 
                fontWeight: '700',
                marginBottom: '12px' 
              }}>
                Get Rewards
              </h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                Being active pays off. Write reviews, post stuff, keep your streak going, win giveaways, all of that 
                earns points. Use those points in the shop for game keys, premium features, badges, and other cool stuff.
              </p>
            </div>
          </div>

          <div style={{
            marginTop: '50px',
            padding: '40px',
            backgroundColor: 'rgba(102, 126, 234, 0.1)',
            borderRadius: '16px',
            border: '2px solid var(--btn-primary)'
          }}>
            <h3 style={{
              color: 'var(--btn-primary)',
              fontSize: '24px',
              fontWeight: '700',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              🎯 For Indie Developers
            </h3>
            <p style={{ marginBottom: '16px' }}>
              If you're a developer, you can showcase your game here with screenshots, trailers, store links, and descriptions. 
              Host giveaways to create hype before launch. Get feedback through reviews, helps you see what's working and what isn't.
            </p>
            <p style={{ marginBottom: '16px' }}>
              Share updates through community cards, talk directly with players, use hashtags so people can find your game. 
              You can track how it's doing with basic analytics, views, engagement, that sort of thing.
            </p>
            <p>
              It's free. No submission fees, no revenue cuts, no hidden costs. I built this to help developers, not to take \n              your money. Check the Developer Resources section if you need tips on marketing or visibility.
            </p>
          </div>

          <div style={{
            marginTop: '40px',
            padding: '30px',
            textAlign: 'center',
            backgroundColor: 'var(--card-bg-secondary)',
            borderRadius: '12px',
            border: '1px solid var(--border-color)'
          }}>
            <h3 style={{
              color: 'var(--text-accent)',
              fontSize: '24px',
              fontWeight: '700',
              marginBottom: '16px'
            }}>
              Ready to Join?
            </h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
              Join the community. Free, straightforward, and built for gamers and developers.
            </p>
            <Link to="/register">
              <button style={{
                fontSize: '18px',
                padding: '16px 48px',
                background: 'linear-gradient(135deg, var(--btn-primary), var(--btn-secondary))',
                color: 'var(--text-light)',
                border: 'none',
                borderRadius: '30px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                textDecoration: 'none',
                display: 'inline-block',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-3px) scale(1.05)';
                e.target.style.boxShadow = '0 12px 32px rgba(102, 126, 234, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0) scale(1)';
                e.target.style.boxShadow = '0 8px 24px rgba(102, 126, 234, 0.4)';
              }}>
                Get Started Now
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
